// Shared DivIcon factories + helpers for the operational Live Map overlay
// (farmbase/target label chips, target crosshair, intent-arrow arrowheads).
import L from 'leaflet';
import type { TargetStatus } from '../../types/domain';

// Operational overlay palette (single source of truth for the new layers).
export const OPS_COLORS = {
  farmbase: '#22d3ee', // tactical cyan (mockup)
  targetLive: '#ef4444', // ACTIVE / ASSIGNED
  targetExecuting: '#f59e0b', // EXECUTING accent (pulse + platoon flip)
  targetInactive: '#6b7280', // eliminated / failed / expired / not-yet-active
  intentAssigned: '#22c55e', // green dashed intent arrow
  intentExecuting: '#f59e0b', // orange solid intent arrow
} as const;

// Target statuses drawn by default; everything else sits behind "show inactive".
export const LIVE_TARGET_STATUSES: TargetStatus[] = ['ACTIVE', 'ASSIGNED', 'EXECUTING'];

export function isLiveTargetStatus(status: TargetStatus): boolean {
  return LIVE_TARGET_STATUSES.includes(status);
}

export function targetStatusColor(status: TargetStatus): string {
  if (status === 'EXECUTING') return OPS_COLORS.targetExecuting;
  if (status === 'ACTIVE' || status === 'ASSIGNED') return OPS_COLORS.targetLive;
  return OPS_COLORS.targetInactive;
}

// How a target should RENDER, not just its raw status:
//   live      — ACTIVE / ASSIGNED / EXECUTING
//   scheduled — INACTIVE whose window hasn't closed (the backend holds status
//               at INACTIVE until openingTime, but a freshly-planned target is
//               exactly what the commander needs to see while planning)
//   ended     — ELIMINATED / FAILED / EXPIRED, or INACTIVE whose closingTime
//               already passed (behind the "Ended targets" toggle)
export type TargetRenderClass = 'live' | 'scheduled' | 'ended';

export function targetRenderClass(
  t: { status: TargetStatus; closingTime?: string | null },
  nowMs: number = Date.now()
): TargetRenderClass {
  if (isLiveTargetStatus(t.status)) return 'live';
  if (t.status === 'INACTIVE') {
    const closing = t.closingTime ? new Date(t.closingTime).getTime() : NaN;
    if (!Number.isNaN(closing) && nowMs > closing) return 'ended';
    return 'scheduled';
  }
  return 'ended';
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c] as string));
}

// "FB-1/PL-1" → "PL-1"; falls back to the full callsign when the tail doesn't match.
export function shortCallsign(callsign?: string | null): string {
  const cs = String(callsign || '').trim();
  const m = cs.match(/(PL-?\d+)\s*$/i);
  return (m ? m[1] : cs || 'PLT').toUpperCase();
}

// "MOVING_OUT" → "MOVING OUT".
export function humanizePhase(phase?: string | null): string {
  return String(phase || '').replace(/_/g, ' ').trim().toUpperCase();
}

// Permanent operational label chip (title + optional sub-line), anchored so it
// floats ABOVE its map point. Dark chip, white title, accent-colored sub-line —
// matches the existing dark map aesthetic.
export function labelChipIcon(opts: {
  title: string;
  sub?: string;
  color: string;
  dim?: boolean;
}): L.DivIcon {
  const { title, sub, color, dim } = opts;
  const html = `
    <div style="transform:translate(-50%,calc(-100% - 10px));display:inline-block;opacity:${dim ? 0.55 : 1};
      background:rgba(10,14,20,.92);border:1px solid ${color};border-left:3px solid ${color};
      border-radius:4px;padding:2px 6px;box-shadow:0 2px 6px rgba(0,0,0,.6);text-align:left;">
      <div style="font:700 10px/1.2 'Share Tech Mono',monospace;color:#fff;white-space:nowrap;
        text-shadow:0 1px 2px rgba(0,0,0,.8);">${escapeHtml(title)}</div>
      ${sub
        ? `<div style="font:600 8px/1.3 'Share Tech Mono',monospace;color:${color};white-space:nowrap;
             letter-spacing:.5px;">${escapeHtml(sub)}</div>`
        : ''}
    </div>`;
  return L.divIcon({ className: 'ops-label-chip', html, iconSize: [0, 0], iconAnchor: [0, 0] });
}

// Red crosshair target marker; EXECUTING targets get infinite CSS pulse rings
// (keyframes `contact-pulse-ring` live in index.css). `dashed`/`opacity` give
// SCHEDULED targets their "planned" outline look without the ended-gray dim.
export function targetCrosshairIcon(opts: {
  color: string;
  dim?: boolean;
  pulse?: boolean;
  dashed?: boolean;
  opacity?: number;
}): L.DivIcon {
  const { color, dim, pulse } = opts;
  const alpha = opts.opacity ?? (dim ? 0.5 : 1);
  const dash = opts.dashed ?? dim;
  const ring = (delay: string) =>
    `<div class="contact-pulse-ring" style="position:absolute;inset:-10px;border-radius:50%;
       border:2px solid ${color};animation-delay:${delay};"></div>`;
  const html = `
    <div style="position:relative;width:26px;height:26px;opacity:${alpha};">
      ${pulse ? ring('0ms') + ring('600ms') : ''}
      <div style="position:absolute;inset:2px;border:2px solid ${color};border-radius:50%;
        background:${color}22;box-shadow:0 1px 4px rgba(0,0,0,.5);${dash ? 'border-style:dashed;' : ''}"></div>
      <div style="position:absolute;left:50%;top:-2px;height:8px;width:2px;background:${color};transform:translateX(-50%);"></div>
      <div style="position:absolute;left:50%;bottom:-2px;height:8px;width:2px;background:${color};transform:translateX(-50%);"></div>
      <div style="position:absolute;top:50%;left:-2px;width:8px;height:2px;background:${color};transform:translateY(-50%);"></div>
      <div style="position:absolute;top:50%;right:-2px;width:8px;height:2px;background:${color};transform:translateY(-50%);"></div>
      <div style="position:absolute;left:50%;top:50%;width:4px;height:4px;border-radius:50%;background:${color};
        transform:translate(-50%,-50%);"></div>
    </div>`;
  return L.divIcon({ className: 'target-crosshair-marker', html, iconSize: [26, 26], iconAnchor: [13, 13] });
}

// Arrowhead at the target end of an intent line, rotated to the line's bearing
// (0° = North, clockwise — same convention as lib/geo bearingDeg).
export function arrowheadIcon(bearingDegrees: number, color: string, opacity = 1): L.DivIcon {
  const html = `
    <div style="transform:translate(-50%,-50%) rotate(${Math.round(bearingDegrees)}deg);width:18px;height:18px;
      opacity:${opacity};display:flex;align-items:flex-end;justify-content:center;">
      <div style="width:0;height:0;border-left:6px solid transparent;border-right:6px solid transparent;
        border-bottom:12px solid ${color};filter:drop-shadow(0 1px 2px rgba(0,0,0,.6));"></div>
    </div>`;
  return L.divIcon({ className: 'intent-arrowhead', html, iconSize: [0, 0], iconAnchor: [0, 0] });
}
